import { useState } from "react";
import { MasteryBar } from "../../components/MasteryBar";
import { PaperToggle, type PaperType } from "../../components/PaperToggle";
import type { ExamChoice } from "../../lib/catalog";
import { BackButton } from "../../components/BackButton";

export interface TopicOption {
  id: string;
  name: string;
  /** null when never attempted. */
  mastery: number | null;
  weak?: boolean;
}

interface TopicPickScreenProps {
  subject: string;
  exam: ExamChoice;
  topics: TopicOption[];
  onPick: (topicId: string, paper: PaperType) => void;
}

/**
 * Screen 16b — weakest first.
 *
 * The order is the recommendation. A student who opens this screen and taps the
 * first thing should be practising the right thing, which is why "Needs work"
 * is a section rather than a badge scattered through an alphabetical list.
 */
export function TopicPickScreen({ subject, exam, topics, onPick }: TopicPickScreenProps) {
  const [paper, setPaper] = useState<PaperType>("objective");

  const weak = topics.filter((topic) => topic.weak);
  const rest = topics.filter((topic) => !topic.weak);

  const row = (topic: TopicOption) => (
    <li key={topic.id}>
      <button
        type="button"
        onClick={() => onPick(topic.id, paper)}
        className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-left"
      >
        <span className="text-[14px]">{topic.name}</span>
        <MasteryBar mastery={topic.mastery} weak={topic.weak} />
      </button>
    </li>
  );

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/practice" />

      <p className="m-0 pt-3 text-[12px] text-text-muted">{subject} · Topics</p>
      <h1 className="m-0 pt-1 font-[family-name:var(--font-display)] text-[20px] font-bold">
        Weakest first
      </h1>
      <p className="m-0 pt-1 text-[12.5px] text-text-muted">Tap one to start.</p>

      <div className="pt-4">
        <PaperToggle value={paper} onChange={setPaper} hidden={exam === "JAMB"} />
      </div>

      {weak.length > 0 ? (
        <>
          <h2 className="m-0 pt-5 text-[11px] font-semibold uppercase tracking-[0.1em] text-negative">
            Needs work
          </h2>
          <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-2.5">{weak.map(row)}</ul>
        </>
      ) : null}

      <h2 className="m-0 pt-5 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
        All topics
      </h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-2.5">{rest.map(row)}</ul>

      <p className="m-0 pt-5 text-[11.5px] leading-[1.45] text-text-muted">
        Topic sets mix past questions with generated ones — internet needed for generated.
      </p>
    </div>
  );
}
